import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Star, Camera, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export function ProductReviews({ productId }) {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], avg: 0, count: 0 });
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(
    () => api.get(`/products/${productId}/reviews`).then((response) => setData(response.data)),
    [productId],
  );

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/products/${productId}/reviews`, { rating, text, photo });
      toast.success("Thanks for your review!");
      setText(""); setPhoto(""); setRating(5);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <h3 className="font-display font-semibold">Reviews</h3>
        {data.count > 0 && (
          <span className="text-xs text-stone-500 font-mono">
            ★ {data.avg} · {data.count} reviews
          </span>
        )}
      </div>

      {user && user !== false && (
        <form onSubmit={submit} className="mt-3 rounded-xl bg-stone-50 p-3">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                type="button"
                key={n}
                data-testid={`star-${n}`}
                onClick={() => setRating(n)}
                className={n <= rating ? "text-orange-500" : "text-stone-300"}
              >
                <Star size={20} fill={n <= rating ? "#F97316" : "none"} />
              </button>
            ))}
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            placeholder="Share your experience..."
            className="mt-2 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
          />
          <div className="mt-2 flex items-center gap-2">
            <label className="inline-flex items-center gap-1 rounded-full border border-stone-300 px-3 py-1 text-xs cursor-pointer hover:bg-white">
              <Camera size={12} /> Add photo
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  if (f.size > 2 * 1024 * 1024) { toast.error("Max 2 MB"); return; }
                  const rd = new FileReader();
                  rd.onload = () => setPhoto(String(rd.result));
                  rd.readAsDataURL(f);
                }}
              />
            </label>
            {photo && (
              <div className="relative">
                <img src={photo} alt="preview" className="h-10 w-10 rounded object-cover" />
                <button type="button" onClick={() => setPhoto("")} className="absolute -right-1 -top-1 rounded-full bg-stone-900 p-0.5 text-white">
                  <X size={10} />
                </button>
              </div>
            )}
            <button disabled={busy} className="ml-auto rounded-full bg-orange-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-orange-700 disabled:opacity-50">
              {busy ? "Posting..." : "Post review"}
            </button>
          </div>
          <p className="mt-1 text-[10px] text-stone-500">Only customers who received this product can review.</p>
        </form>
      )}

      <div className="mt-3 space-y-2">
        {data.reviews.length === 0 && <p className="text-xs text-stone-500">No reviews yet — be the first!</p>}
        {data.reviews.map((r) => (
          <div key={r.id} className="rounded-lg border border-stone-100 p-3">
            <div className="flex items-center gap-2">
              <div className="flex text-orange-500">
                {[1,2,3,4,5].map((n) => <Star key={n} size={12} fill={n <= r.rating ? "#F97316" : "none"} />)}
              </div>
              <p className="text-xs font-semibold">{r.user_name}</p>
              <p className="ml-auto text-[10px] text-stone-400">{new Date(r.at).toLocaleDateString()}</p>
            </div>
            {r.text && <p className="mt-1 text-sm text-stone-700">{r.text}</p>}
            {r.photo && <img src={r.photo} alt="" className="mt-2 h-24 w-24 rounded-lg object-cover" />}
          </div>
        ))}
      </div>
    </div>
  );
}
